import { AppContent, Content, effectiveImageQuality } from 'common/types'
import { LayoutStyleObjectFit } from '../../../../src/types/layout.types'
import { getAppUrl, resolveImageUrl } from 'common/utils'
import { StreamingVideoPlayer } from './VideoPlayer'
import { getNativeMediaUrl } from '../../native-bridge'
import { useScreen } from './ScreenContainer'

interface ContentViewerProps {
  content: Content
  preload?: boolean
  seek?: number // in milliseconds
  objectFit?: LayoutStyleObjectFit
}

interface AppPlayerProps {
  content: AppContent
  preload?: boolean
}

const AppPlayer = ({ content, preload }: AppPlayerProps) => {
  const metadata = preload
    ? {
        ...content.metadata,
        muted: true, // apps without mute behaviour shall ignore this param
      }
    : content.metadata

  return (
    <iframe
      src={getAppUrl(content.format, metadata)}
      title={content.name}
      allow="autoplay; encrypted-media; fullscreen;"
      className="w-full h-full border-none"
    />
  )
}

// WebOS TVs have a single-decoder video pipeline — a second actively-playing
// `<video>` element (even muted, for preload) pauses the one currently on
// screen. Skip rendering the preload component for video content on those
// platforms; the proxy's media cache still smooths transitions after the
// first playlist cycle.
function shouldSkipVideoPreload(): boolean {
  return window.NativeBridge?.platform === 'webos'
}

// return seek time in seconds
// if content is played longer than its duration it will be looped and it is possible that seek time is beyond duration
// in that case we normalize the seek time to be within duration
function normalizeSeekTime(seekInMs: number | undefined, content: Content): number | undefined {
  const seekInSeconds = seekInMs ? Math.floor(seekInMs / 1000) : undefined
  if ('duration' in content && content.duration && seekInSeconds) {
    if (seekInSeconds > content.duration) {
      return seekInSeconds % content.duration
    }
  }
  return seekInSeconds
}

export const ContentViewer = ({
  content,
  preload = false,
  seek,
  objectFit = LayoutStyleObjectFit.FILL,
}: ContentViewerProps) => {
  const normalizedSeek = normalizeSeekTime(seek, content)
  const screen = useScreen()

  if (content.type === 'image') {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <img
          src={getNativeMediaUrl(resolveImageUrl(content.url, content.urls, effectiveImageQuality(screen ?? {})))}
          alt={content.name}
          className="max-w-full max-h-full w-full h-full"
          style={{
            objectFit: objectFit,
          }}
        />
      </div>
    )
  } else if (content.type === 'video') {
    if (preload && shouldSkipVideoPreload()) {
      return null
    }
    return <StreamingVideoPlayer content={content} preload={preload} startTime={normalizedSeek} objectFit={objectFit} />
  } else if (content.type === 'app') {
    return <AppPlayer content={content} preload={preload} />
  } else {
    return (
      <div className="w-full h-full flex justify-center items-center bg-gradient-to-br from-slate-900 to-slate-700 text-white font-sans">
        <div>Unsupported content type: {content.type}</div>
      </div>
    )
  }
}
